import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ExcavationAreaPicker } from "./ExcavationAreaPicker";

export const WORK_TYPES = ["Trenching", "General excavation", "Potholing / vacuum excavation", "Directional drilling / boring", "Road works", "Footings / piling", "Fencing / posts", "Landscaping", "Other"];
const field = "h-11 w-full rounded-md border border-border bg-background px-3 text-sm";

function initial(defaults, existing) {
  if (existing) return { ...existing };
  const lat = Number(defaults.lat), lng = Number(defaults.lng);
  const mapped = Number.isFinite(lat) && Number.isFinite(lng) && lat < -29 && lat > -53;
  return { address: defaults.address || "", area: mapped ? { lat, lng, radius_m: 25 } : null, planned_start: defaults.planned_start || "",
    planned_end: defaults.planned_end || "", work_type: "", description: "", contact: { ...defaults.contact } };
}

export function EnquiryForm({ jobId, defaults, existing, onSaved, onCancel }) {
  const [f, setF] = useState(() => initial(defaults, existing));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setC = (k) => (e) => setF((s) => ({ ...s, contact: { ...s.contact, [k]: e.target.value } }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { address: f.address, area: f.area, planned_start: f.planned_start || null, planned_end: f.planned_end || null,
      work_type: f.work_type, description: f.description, contact: f.contact };
    try {
      const { data } = existing
        ? await api.patch(`/jobs/${jobId}/underground-enquiries/${existing.id}`, body, { __flOnlineOnly: true })
        : await api.post(`/jobs/${jobId}/underground-enquiries`, body, { __flOnlineOnly: true });
      toast.success("Enquiry details saved");
      onSaved(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg : d || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="space-y-4" data-testid="bud-enquiry-form">
      <label className="block text-sm font-medium">Worksite address<Input value={f.address} onChange={set("address")} className="mt-1 h-11" data-testid="bud-address-input" /></label>
      <div><p className="text-sm font-medium mb-1">Proposed excavation area</p>
        <ExcavationAreaPicker area={f.area} onChange={(area) => setF((s) => ({ ...s, area }))} /></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block text-sm font-medium">Planned start<input type="date" required value={f.planned_start || ""} onChange={set("planned_start")} className={`${field} mt-1`} data-testid="bud-start-input" /></label>
        <label className="block text-sm font-medium">Planned finish<input type="date" value={f.planned_end || ""} onChange={set("planned_end")} className={`${field} mt-1`} data-testid="bud-end-input" /></label>
      </div>
      <label className="block text-sm font-medium">Work type
        <select required value={f.work_type} onChange={set("work_type")} className={`${field} mt-1`} data-testid="bud-worktype-select">
          <option value="">Choose…</option>{WORK_TYPES.map((w) => <option key={w}>{w}</option>)}
        </select></label>
      <label className="block text-sm font-medium">Description of work
        <textarea value={f.description} onChange={set("description")} rows={3} maxLength={2000} className="mt-1 w-full rounded-md border border-border bg-background p-3 text-sm" placeholder="e.g. 40 m trench, 1.2 m deep, for new stormwater line" data-testid="bud-description-input" /></label>
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="block text-sm font-medium">Contact name<Input value={f.contact.name} onChange={setC("name")} className="mt-1 h-11" data-testid="bud-contact-name" /></label>
        <label className="block text-sm font-medium">Phone<Input value={f.contact.phone} onChange={setC("phone")} className="mt-1 h-11" data-testid="bud-contact-phone" /></label>
        <label className="block text-sm font-medium">Email<Input value={f.contact.email} onChange={setC("email")} className="mt-1 h-11" data-testid="bud-contact-email" /></label>
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="bud-form-cancel">Cancel</Button>
        <Button type="submit" disabled={busy || !f.area} className="bg-[#FF5F15] hover:bg-[#E65100] text-white" data-testid="bud-form-save">{busy ? "Saving…" : "Save draft"}</Button>
      </div>
    </form>
  );
}
